import { createApi } from '@reduxjs/toolkit/query/react';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import type { IShipmentTemplate } from 'src/shipment/templates/ShipmentTemplate';
import { baseQueryWithAuth } from '../rtkq.interceptor';

const templatesTag = 'templates';

/** RTK Query API slice for shipment template endpoints. */
export const templatesApi = createApi({
  reducerPath: 'templatesApi',
  tagTypes: [templatesTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch all shipment templates. */
    getAllTemplates: builder.query<Array<IShipmentTemplate>, void>({
      providesTags: [templatesTag],
      query: () => apiResources.shipmentTemplate,
      ...transformResponses,
    }),
    /** Query to fetch a single shipment template by ID. */
    getTemplate: builder.query<IShipmentTemplate, number>({
      providesTags: [templatesTag],
      query: (id) => `${apiResources.shipmentTemplate}/${id}/template`,
      ...transformResponses,
    }),
    /** Mutation to create a new shipment template. */
    createTemplate: builder.mutation<IShipmentTemplate, Omit<IShipmentTemplate, 'templateId'>>({
      query: (body) => ({ url: apiResources.shipmentTemplate, method: 'POST', body }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [templatesTag]),
    }),
    /** Mutation to update an existing shipment template. */
    updateTemplate: builder.mutation<string, IShipmentTemplate>({
      query: ({ templateId, ...patch }) => ({
        url: apiResources.shipmentTemplate + `/${templateId}`,
        method: 'PATCH',
        body: patch,
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [templatesTag]),
    }),
    /** Mutation to delete a shipment template by ID. */
    deletedTemplate: builder.mutation<string, number>({
      query: (id) => ({ url: apiResources.shipmentTemplate + `/${id}`, method: 'DELETE' }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [templatesTag]),
    }),
  }),
});

/** RTK Query hooks for shipment template endpoints. */
export const {
  useGetAllTemplatesQuery,
  useLazyGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeletedTemplateMutation,
} = templatesApi;
